/**
 * =============================================================================
 * IDS CRM Web - Jobs & Tickets by engineer
 * =============================================================================
 *
 * Gareth, 20 October 2026: a view with one column per engineer showing all
 * their open work - reactive tickets, engineer work and maintenance - and an
 * Unassigned column first. Managers, team leaders and operations coordinators
 * drag a card onto an engineer to give them the work; the drop asks for the
 * date it must be completed by (filled in with the current one, can be
 * changed), and the engineer is told both.
 *  - A job with several engineers shows in each of their columns. Dragging
 *    one of those cards to another engineer swaps the engineer it came from
 *    for the new one; everyone else stays, and the lead stays the lead unless
 *    it was the lead who moved.
 *  - Nothing is dropped on Unassigned, or on someone who cannot take work
 *    (their column shows only because they still hold some).
 *  - Each card also has a Move button, which opens the same dialog with an
 *    engineer to choose: dragging does not work on a phone or tablet.
 * Saves go through saveAllocation (BoardAssign.js), the same routes as the
 * board's details panel.
 * =============================================================================
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CompleteByField, completeByProblem, saveAllocation, useBookingWarnings } from './BoardAssign';
import { CardPanel, detailPathFor } from './WorkBoard';
import { formatDateKey, ukTodayKey } from '../utils/dates';
import { useColumnOrder, isColumnDrag } from '../hooks/useColumnOrder';
import { useOnOpsBoard } from './OpsBoardServiceStatus';
import { NOTE_TYPE_FOR_WORK } from './ReviewNotes';
import '../pages/OpsBoard.css';
import './WorkBoard.css';

const UNASSIGNED = '__unassigned';
export const TYPE_FILTERS = [
  ['all', 'All work'],
  ['reactive_ticket', 'Tickets'],
  ['scheduled_work', 'Engineer work'],
  ['scheduled_maintenance', 'Maintenance']
];
const TYPE_BADGES = {
  reactive_ticket: ['ticket', 'Ticket'],
  scheduled_work: ['engineer', 'Engineer work'],
  scheduled_maintenance: ['maintenance', 'Maintenance']
};

/**
 * The engineers on a piece of work after it moves from one column to another:
 * from Unassigned it is just the new engineer; otherwise the engineer it came
 * from is swapped for the new one in place (so a moved lead's place goes to
 * the new engineer), and nobody is listed twice. A ticket has one engineer.
 */
export function engineersAfterMove(card, fromId, toId) {
  if (card.work_type === 'reactive_ticket' || !fromId || fromId === UNASSIGNED) return [toId];
  const current = Array.isArray(card.engineer_ids) ? card.engineer_ids : [];
  const swapped = current.includes(fromId) ? current.map(userId => (userId === fromId ? toId : userId)) : [toId, ...current];
  return swapped.filter((userId, index) => swapped.indexOf(userId) === index);
}

const matches = (card, text) => {
  if (!text) return true;
  const hay = [card.ref, card.title, card.customer_name, card.asset_ref].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(text.toLowerCase());
};

export function dueLine(card, today = ukTodayKey()) {
  if (!card.due_key) return 'No completion date';
  const otherYear = card.due_key.slice(0, 4) !== today.slice(0, 4);
  const weekday = formatDateKey(card.due_key, { weekday: 'short' });
  const dayMonth = formatDateKey(card.due_key, { day: 'numeric', month: 'short' });
  const day = `${weekday} ${dayMonth}${otherYear ? ` ${card.due_key.slice(0, 4)}` : ''}`;
  return `Complete by ${day}${card.due_time ? ` ${card.due_time}` : ''}`;
}

/**
 * A card from this view in the shape the Board's details panel reads
 * (CardPanel in WorkBoard.js): the engineers' names, the lead or the ticket's
 * engineer as assigned_to_id, the stage label, and the job's work date.
 */
export function panelCardFor(card, engineers) {
  const ids = Array.isArray(card.engineer_ids) ? card.engineer_ids : [];
  const nameOf = (userId) => (engineers.find(engineer => engineer.user_id === userId) || {}).full_name;
  return {
    ...card,
    asset_refs: card.asset_ref,
    assigned_to_id: ids[0] || null,
    assigned_to_name: ids.map(nameOf).filter(Boolean).join(', '),
    assigned_to_planning_group: card.work_type === 'scheduled_maintenance' && ids.length === 0,
    work_day: card.work_type === 'reactive_ticket' ? null : card.due_key
  };
}

function EngineerCard({ card, columnId, isLead, canMove, onDragStart, onMove, opsColumn, selected, onSelect }) {
  const [badgeClass, badgeText] = TYPE_BADGES[card.work_type] || ['', ''];
  const path = detailPathFor(card);
  // Clicking the card opens its details at the bottom, as on the Board
  // (Gareth 2026-10-21); the reference still goes straight to its page.
  const select = () => onSelect(card);
  return (
    <div
      className={`ops-card work-card engineer-card${canMove ? ' draggable' : ''}${selected ? ' selected' : ''}`}
      draggable={canMove}
      onDragStart={canMove ? (event) => onDragStart(event, card, columnId) : undefined}
      onClick={select}
      onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); select(); } }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${card.ref} ${card.title}`}
      data-ref={card.ref}
    >
      <span className="ops-card-top">
        {path ? <Link to={path} className="ops-card-ref" onClick={(event) => event.stopPropagation()}>{card.ref}</Link> : <span className="ops-card-ref">{card.ref}</span>}
        <span className={`work-type-badge ${badgeClass}`}>{badgeText}</span>
        {isLead && <span className="engineer-card-lead">Lead</span>}
        {opsColumn && <span className="work-ops-tag" title={`On the Ops Planning board: ${opsColumn}`}>Ops board</span>}
      </span>
      <span className="ops-card-title">{card.title}</span>
      <span className="ops-card-line">{[card.customer_name, card.asset_ref].filter(Boolean).join(' · ') || 'No customer'}</span>
      <span className="ops-card-meta ops-card-plan">
        <span className={`engineer-card-stage ops-colour-${card.stage_colour}`}>{card.stage_label}</span>
        <span className={card.overdue ? 'engineer-card-due overdue' : 'engineer-card-due'}>{dueLine(card)}</span>
      </span>
      {canMove && (
        <button type="button" className="ops-link-btn engineer-card-move" onClick={(event) => { event.stopPropagation(); onMove(card, columnId); }} aria-label={`Move ${card.ref}`}>
          Move…
        </button>
      )}
    </div>
  );
}

/** The dialog a drop (or Move) opens: who, the completion date, Confirm. */
function MoveDialog({ move, engineers, onCancel, onSaved }) {
  const { authFetch } = useAuth();
  const { card, fromId } = move;
  const [toId, setToId] = useState(move.toId || '');
  const [dateKey, setDateKey] = useState(card.due_key || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const nameOf = (userId) => (engineers.find(engineer => engineer.user_id === userId) || {}).full_name || 'Unknown engineer';
  const takers = engineers.filter(engineer => engineer.can_take_work && engineer.user_id !== fromId);
  const after = toId ? engineersAfterMove(card, fromId, toId) : [];
  const alreadyOn = Boolean(toId) && card.work_type !== 'reactive_ticket' && (card.engineer_ids || []).includes(toId) && toId !== fromId;
  const problem = completeByProblem(dateKey);
  const when = useMemo(() => ({
    date: dateKey,
    start: card.all_day ? '' : (card.due_time || ''),
    duration: card.duration_mins || null,
    allDay: Boolean(card.all_day)
  }), [card, dateKey]);
  const noteFor = useBookingWarnings(card, when, toId ? [toId] : []);
  const fromName = fromId && fromId !== UNASSIGNED ? nameOf(fromId) : '';

  const confirm = async () => {
    if (!toId || problem) return;
    setSaving(true);
    setError('');
    try {
      await saveAllocation(authFetch, card, { engineerIds: after, dateKey });
      onSaved(`${card.ref} is now with ${nameOf(toId)}, complete by ${formatDateKey(dateKey)}.`);
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? 'The change could not be saved. Check your signal and try again.' : err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal-content engineer-move-modal" role="dialog" aria-modal="true" aria-labelledby="engineer-move-title">
        <div className="modal-header">
          <h2 id="engineer-move-title">{fromName ? 'Move' : 'Assign'} {card.ref}</h2>
          <button type="button" className="modal-close" onClick={onCancel} disabled={saving} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body">
          <p className="engineer-move-about"><strong>{card.title}</strong>{card.customer_name ? ` · ${card.customer_name}` : ''}</p>
          <div className="board-assign-date">
            <label htmlFor="engineer-move-to">Engineer</label>
            <select id="engineer-move-to" className="board-assign-single" value={toId} onChange={(e) => setToId(e.target.value)} disabled={saving}>
              <option value="">Choose an engineer</option>
              {takers.map(engineer => <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>)}
            </select>
            {toId && noteFor(toId)}
          </div>
          {fromName && toId && !alreadyOn && <p className="ops-muted engineer-move-note">{fromName} comes off it{card.work_type !== 'reactive_ticket' && after.length > 1 ? `; ${after.filter(userId => userId !== toId).map(nameOf).join(', ')} stay${after.length > 2 ? '' : 's'} on it` : ''}.</p>}
          {alreadyOn && <p className="ops-muted engineer-move-note">{nameOf(toId)} is already on this job, so {fromName} just comes off it.</p>}
          <CompleteByField
            id="engineer-move-date"
            value={dateKey}
            onChange={setDateKey}
            savedKey={card.due_key || ''}
            disabled={saving}
            hint={card.work_type === 'reactive_ticket'
              ? 'This moves the ticket\'s SLA resolution due to that day.'
              : card.work_type === 'scheduled_work' ? 'The day the job is booked for (its time stays the same).' : 'The day the visit is due.'}
          />
          {error && <p className="board-assign-error">{error}</p>}
          <div className="engineer-move-actions">
            <button type="button" className="btn-secondary btn-small" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary btn-small" onClick={confirm} disabled={saving || !toId || Boolean(problem)}>
              {saving ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EngineerBoard() {
  const { authFetch } = useAuth();
  // Each person's own order of the engineer columns (Unassigned stays first).
  const columnOrder = useColumnOrder('engineers');
  const [data, setData] = useState(null);
  // Which of these are on the Ops Planning board, read with each load.
  const onOpsBoard = useOnOpsBoard(data);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [move, setMove] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [done, setDone] = useState('');
  // The card whose details are open at the bottom ({ work_type, id }).
  const [selectedKey, setSelectedKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch('/api/v1/work-board/by-engineer');
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || 'The work could not be read.');
      setData(body);
    } catch (err) {
      setData(null);
      setError(err.message === 'Failed to fetch' ? 'The work could not be read. Check your signal and reload the page.' : err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const canAssign = data?.can_assign === true;
  const engineers = useMemo(() => data?.engineers || [], [data]);
  // The open card, fresh from the latest load (it may have moved column); it
  // closes by itself when the work has left the view.
  const selectedCard = useMemo(() => (selectedKey && data
    ? (data.data || []).find(card => card.work_type === selectedKey.work_type && card.id === selectedKey.id) || null
    : null), [data, selectedKey]);
  // Stage labels for the panel, from the cards themselves.
  const stageColumns = useMemo(() => {
    const seen = new Map();
    (data?.data || []).forEach(card => { if (!seen.has(card.status)) seen.set(card.status, { value: card.status, label: card.stage_label }); });
    return [...seen.values()];
  }, [data]);

  const columns = useMemo(() => {
    const cards = (data?.data || []).filter(card => (type === 'all' || card.work_type === type) && matches(card, search.trim()));
    const byColumn = new Map([[UNASSIGNED, []], ...engineers.map(engineer => [engineer.user_id, []])]);
    for (const card of cards) {
      const ids = card.engineer_ids || [];
      if (ids.length === 0) byColumn.get(UNASSIGNED).push(card);
      for (const userId of ids) if (byColumn.has(userId)) byColumn.get(userId).push(card);
    }
    return [
      { id: UNASSIGNED, label: 'Unassigned', colour: 'pink', canTake: false, cards: byColumn.get(UNASSIGNED) },
      ...columnOrder.arrange(engineers, engineer => engineer.user_id).map(engineer => ({
        id: engineer.user_id,
        label: engineer.full_name,
        colour: engineer.can_take_work ? 'grey' : 'brown',
        canTake: engineer.can_take_work,
        cards: byColumn.get(engineer.user_id)
      }))
    ];
  }, [data, engineers, type, search, columnOrder]);
  const engineerKeys = columns.filter(column => column.id !== UNASSIGNED).map(column => column.id);

  const onDragStart = (event, card, columnId) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', JSON.stringify({ work_type: card.work_type, id: card.id, from: columnId }));
  };

  const readDrag = (event) => {
    try {
      const parsed = JSON.parse(event.dataTransfer.getData('text/plain'));
      const card = (data?.data || []).find(entry => entry.work_type === parsed.work_type && entry.id === parsed.id);
      return card ? { card, fromId: parsed.from } : null;
    } catch (err) {
      return null;
    }
  };

  const onDrop = (event, column) => {
    event.preventDefault();
    setDropTarget(null);
    if (!canAssign || !column.canTake) return;
    const dragged = readDrag(event);
    if (!dragged || dragged.fromId === column.id) return;
    setDone('');
    setMove({ card: dragged.card, fromId: dragged.fromId, toId: column.id });
  };

  const today = ukTodayKey();
  const overdueCount = (data?.data || []).filter(card => card.due_key && card.due_key < today).length;

  return (
    <div className="work-board engineer-board">
      <div className="work-board-bar">
        <p className="ops-muted engineer-board-intro">
          {canAssign ? 'Drag a card onto an engineer to give them the work. You will be asked for the completion date.' : 'Open work by engineer.'}
        </p>
        <div className="work-board-filters">
          <div className="work-board-sla-slot">
            <select aria-label="Kind of work" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPE_FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <input type="search" placeholder="Search ref, customer or robot" aria-label="Search the work" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      {/* One line, always there: the last change on the left, the count and
          "Usual column order" on the right, so neither moves the columns
          (Gareth 2026-10-20). */}
      <p className="work-board-count ops-muted engineer-board-count">
        <span className="board-assign-done engineer-board-done" role="status">{done}</span>
        <span className="engineer-board-count-right">
          {data
            ? <>{data.data.length} open{overdueCount ? ` · ${overdueCount} past their date` : ''}{data.capped ? ' (showing the oldest)' : ''}{canAssign ? '' : ' · read only'}</>
            : ' '}
          {data && columnOrder.customised && (
            <> · <button type="button" className="ops-link-btn work-board-reset-order" onClick={columnOrder.reset}>Usual column order</button></>
          )}
        </span>
      </p>
      {columnOrder.error && <p className="ops-error">{columnOrder.error}</p>}
      {error && <p className="ops-error">{error}</p>}
      {loading && !data && <p className="ops-muted">Loading the work…</p>}

      {data && (
        <div className="ops-board work-board-columns engineer-board-columns" role="region" aria-label="Work by engineer">
          {columns.map(column => (
            <section
              key={column.id}
              className={`ops-column ops-colour-${column.colour}${dropTarget === column.id ? ' engineer-drop-target' : ''}${column.id !== UNASSIGNED && columnOrder.over === column.id && columnOrder.dragging !== column.id ? ' column-drop-target' : ''}${columnOrder.dragging === column.id ? ' column-dragging' : ''}`}
              aria-label={column.label}
              onDragOver={(event) => {
                // A column being moved (never onto Unassigned), else a card.
                if (column.id !== UNASSIGNED && columnOrder.dragOver(event, column.id)) return;
                if (isColumnDrag(event) || !canAssign || !column.canTake) return;
                event.preventDefault();
                setDropTarget(column.id);
              }}
              onDragLeave={canAssign && column.canTake ? () => setDropTarget(current => (current === column.id ? null : current)) : undefined}
              onDrop={(event) => {
                if (column.id !== UNASSIGNED && columnOrder.drop(event, column.id, engineerKeys)) return;
                if (isColumnDrag(event)) return;
                onDrop(event, column);
              }}
            >
              <header
                className={`ops-column-head${column.id !== UNASSIGNED ? ' column-head-movable' : ''}`}
                {...(column.id !== UNASSIGNED ? columnOrder.headerProps(column.id) : {})}
              >
                <h2>{column.label}</h2>
                <span className="ops-count">{column.cards.length}</span>
              </header>
              {!column.canTake && column.id !== UNASSIGNED && <p className="ops-muted engineer-column-note">Cannot take new work</p>}
              <div className="ops-column-cards">
                {column.cards.map(card => (
                  <EngineerCard
                    key={`${card.work_type}-${card.id}`}
                    card={card}
                    columnId={column.id}
                    isLead={card.work_type === 'scheduled_work' && (card.engineer_ids || []).length > 1 && card.engineer_ids[0] === column.id}
                    canMove={canAssign}
                    onDragStart={onDragStart}
                    onMove={(moved, fromId) => { setDone(''); setMove({ card: moved, fromId, toId: '' }); }}
                    opsColumn={onOpsBoard.get(`${NOTE_TYPE_FOR_WORK[card.work_type]}:${card.id}`)}
                    selected={Boolean(selectedCard) && selectedCard.work_type === card.work_type && selectedCard.id === card.id}
                    onSelect={(picked) => setSelectedKey({ work_type: picked.work_type, id: picked.id })}
                  />
                ))}
                {column.cards.length === 0 && <p className="ops-empty">Nothing here</p>}
              </div>
            </section>
          ))}
        </div>
      )}

      {data && (
        <div className="ops-panel work-board-panel">
          <div className="ops-panel-bar"><strong>Details and review notes</strong></div>
          {selectedCard ? (
            <CardPanel
              card={panelCardFor(selectedCard, engineers)}
              columns={stageColumns}
              atRiskHours={4}
              onClose={() => setSelectedKey(null)}
              canAssign={canAssign}
              engineers={engineers.filter(engineer => engineer.can_take_work)}
              onAssigned={load}
            />
          ) : (
            <div className="ops-panel-body"><p className="ops-muted">Click a card to see its details and review notes.</p></div>
          )}
        </div>
      )}

      {move && (
        <MoveDialog
          move={move}
          engineers={engineers}
          onCancel={() => setMove(null)}
          onSaved={(message) => { setMove(null); setDone(message); load(); }}
        />
      )}
    </div>
  );
}

export default EngineerBoard;
